import { Injectable } from '@angular/core';
import { CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of, merge, fromEvent } from 'rxjs';
import { OrderedEditorComponent } from '../ordered-editor/ordered-editor.component';
import { OrderableEditorComponent } from '../orderable-editor/orderable-editor.component';
import { ConfirmationModalContext } from '../../domain/confirmation-modal-context';
import { NotificationModalType } from '../../domain/notification-modal-type.enum';

import { map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { ModalService } from '../../core/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from 'ngx-web-api';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ThemeService } from 'app/core/services/theme.service';

@Injectable()
export class CanLeaveEditorGuard implements CanDeactivate<OrderedEditorComponent | OrderableEditorComponent> {
  constructor(
    private modalService: ModalService,
    private translateService: TranslateService,
    private accountService: AccountService,
    private ngbModal: NgbModal,
    private themeService: ThemeService
  ) {}

  canDeactivate(
    component: OrderedEditorComponent | OrderableEditorComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.accountService.account$.pipe(
      withLatestFrom(this.themeService.theme),
      map(([account, theme]) => [account.isInstantiated, theme.shouldShowTiledMenu]),
      mergeMap(([loggedIn, hasTiledMenu]) => {
        // Do not block redirection to expiration page since the session is already reset and the order is already lost
        // Same applies when item is being edited and gets deleted from order tree
        if (nextState.url === '/expiration' || !!nextState.root.queryParams['redirectFromDelete']) {
          return of(true);
        }

        // When the target URL is '/menu' then RedirectToFirstMenuChildGuard will ALWAYS redirect user to the first available
        // Panel Tab. In order to avoid duplicate confirmation modals we return true in this case, and will handle all the
        // logic on the subsequent route change which will be like '/menu/firstMenuPanelTab'
        if (nextState.url === '/menu' && !hasTiledMenu) {
          return of(true);
        }

        // The route /account/my-orders is available for not logged in users, but will redirect them to login page,
        // so we need to return true in order to avoid duplicate modals
        if (!loggedIn) {
          if (nextState.url.indexOf('/my-orders') !== -1) {
            return of(true);
          }
        }

        const context = new ConfirmationModalContext();
        context.title = this.translateService.instant('component.item_editor.editor.can_leave_editor_guard.unsaved_changes_modal.title');
        context.submitBtnTitle = this.translateService.instant(
          'component.item_editor.editor.can_leave_editor_guard.unsaved_changes_modal.submitBtnTitle'
        );
        context.submitObservable = of(true);
        context.dismissBtnTitle = this.translateService.instant(
          'component.item_editor.editor.can_leave_editor_guard.unsaved_changes_modal.dismissBtnTitle'
        );
        context.type = NotificationModalType.warning;
        context.textOnly = true;
        context.reverseButtons = true;

        if (!!component && component.canLeaveEditor()) {
          return of(true);
        } else {
          // If a modal (e.g. an upsell/special wizard) is open on top of the editor, close it first so the
          // unsaved-changes confirmation is still shown instead of silently allowing navigation. This also
          // ensures the stray modal does not remain open on navigation (see #32691).
          if (this.ngbModal.hasOpenModals()) {
            this.modalService.closeAllModals();
          }
          const popState$ = fromEvent(window, 'popstate').pipe(
            map(() => true),
            take(1)
          );
          return merge(this.modalService.openConfirmationModal(context).pipe(map(ret => !!ret)), popState$);
        }
      })
    );
  }
}
